/**
 * Social Share Buttons
 * Individual share buttons for Twitter, Facebook, WhatsApp, Email
 */

import { Twitter, Facebook, Share2, Mail } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getShareUrl, openShareWindow } from '../utils/helpers';
import type { SharePlatform } from '../types';

interface SocialShareButtonsProps {
  referralUrl: string;
  referralCode: string;
}

interface ShareButtonConfig {
  id: SharePlatform;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  hoverColor: string;
}

export function SocialShareButtons({ referralUrl, referralCode }: SocialShareButtonsProps) {
  const { t } = useLanguage();

  const shareText = t(
    'referrals.shareText',
    `Check out Rumio - the best way to track IKEA deals and find As-Is bargains! Use my referral code: ${referralCode}`
  );

  const emailSubject = t('referrals.emailSubject', 'Check out Rumio!');

  const buttons: ShareButtonConfig[] = [
    {
      id: 'twitter',
      label: t('referrals.share.x', 'X'),
      icon: Twitter,
      bgColor: 'bg-black',
      hoverColor: 'hover:bg-gray-800',
    },
    {
      id: 'facebook',
      label: t('referrals.share.facebook', 'Facebook'),
      icon: Facebook,
      bgColor: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
    },
    {
      id: 'whatsapp',
      label: t('referrals.share.whatsapp', 'WhatsApp'),
      icon: Share2,
      bgColor: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
    },
    {
      id: 'email',
      label: t('referrals.share.email', 'Email'),
      icon: Mail,
      bgColor: 'bg-gray-600',
      hoverColor: 'hover:bg-gray-700',
    },
  ];

  const handleShare = (platform: SharePlatform) => {
    const url = getShareUrl(platform, referralUrl, shareText, emailSubject);
    if (url) {
      openShareWindow(url);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {t('referrals.quickShare', 'Quick Share')}
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        {t('referrals.shareDescription', 'Share your referral link on social media')}
      </p>

      <div className="grid grid-cols-4 gap-3">
        {buttons.map(({ id, label, icon: Icon, bgColor, hoverColor }) => (
          <button
            key={id}
            onClick={() => handleShare(id)}
            disabled={!referralUrl}
            className={`flex flex-col items-center gap-2 p-4 ${bgColor} ${hoverColor} text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-xs font-medium">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
